import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert, Button, Card, Col, Descriptions, Dropdown, Empty, Image, Menu, message,
    Modal, Progress, Row, Space, Spin, Statistic, Tag, Timeline, Tooltip, Typography
} from "antd";
import {
    ArrowLeftOutlined, BankOutlined, CheckCircleOutlined, ClockCircleOutlined,
    DollarOutlined, DownOutlined, DownloadOutlined, EditOutlined, FileExcelOutlined,
    FileImageOutlined, FilePdfOutlined, FileTextOutlined, HomeOutlined,
    PrinterOutlined, SafetyCertificateOutlined, SendOutlined, TeamOutlined,
    UserOutlined, WalletOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import {useHistory, useParams} from "react-router-dom";
import axiosinstance from "util/Api";
import {CisUI} from "../../../../util/CISUI";

const {Title, Text, Paragraph} = Typography;

const normalize = (v) => String(v || "").trim().toUpperCase();
const money = (v) => Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});
const display = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const pretty = (v) => String(v || "").replace(/_/g, " ").replace(/\b\w/g, x => x.toUpperCase());
const jsonObject = (v) => {
    if (!v) return {};
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return {}; }
};

const LEVEL_LABELS = {
    LOCAL: "Local",
    DISTRICT: "District",
    REGION: "Area",
    AREA: "Area",
    NATIONAL: "National"
};

const STATUS_COLORS = {
    DRAFT: "default",
    SUBMITTED: "blue",
    RESUBMITTED: "cyan",
    UNDER_REVIEW: "processing",
    FORWARDED: "geekblue",
    RETURNED: "orange",
    APPROVED: "green",
    REJECTED: "red",
    CANCELLED: "default"
};

const PRIORITY_COLORS = {
    CRITICAL: "red",
    URGENT: "volcano",
    HIGH: "orange",
    MEDIUM: "blue",
    LOW: "default"
};

const COLORS = {
    navy: "#163B63",
    navyDark: "#102E50",
    blue: "#2B6EA6",
    blueLight: "#EEF5FB",
    green: "#237A57",
    greenLight: "#EEF8F3",
    orange: "#C87516",
    orangeLight: "#FFF7EA",
    red: "#B84545",
    redLight: "#FFF1F0",
    purple: "#6558A6",
    purpleLight: "#F4F2FB",
    text: "#1B2B38",
    secondary: "#667685",
    border: "#DCE4EB",
    lightBorder: "#E9EEF3",
    background: "#EEF2F6",
    white: "#FFFFFF",
    light: "#F8FAFC"
};

const pageCard = {
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    boxShadow: "0 4px 18px rgba(16,46,80,.055)",
    overflow: "hidden"
};

const Section = ({title, subtitle, icon, children, extra}) => (
    <Card
        bordered={false}
        className="asset-detail-section"
        style={{...pageCard, marginBottom: 16}}
        bodyStyle={{padding: 0}}
    >
        <div className="section-heading">
            <div className="section-heading-left">
                <div className="section-icon">{icon}</div>
                <div>
                    <div className="section-title">{title}</div>
                    {subtitle ? <div className="section-subtitle">{subtitle}</div> : null}
                </div>
            </div>
            {extra ? <div>{extra}</div> : null}
        </div>
        <div className="section-body">{children}</div>
    </Card>
);

const KPI = ({label, value, helper, icon, variant = "blue", formatter}) => (
    <div className={`kpi-card kpi-${variant}`}>
        <div className="kpi-top">
            <span className="kpi-label">{label}</span>
            <span className="kpi-icon">{icon}</span>
        </div>
        <div className="kpi-value">
            {formatter ? formatter(value) : money(value)}
        </div>
        {helper ? <div className="kpi-helper">{helper}</div> : null}
    </div>
);

const JsonDescriptions = ({data, moneyKeys = []}) => {
    const entries = Object.entries(data || {}).filter(([key]) => !key.endsWith("_explanation"));
    if (!entries.length) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No information available"/>;
    }

    return (
        <Descriptions bordered size="small" column={{xs: 1, sm: 1, md: 2, xl: 3}} className="report-descriptions">
            {entries.map(([key, value]) => (
                <Descriptions.Item key={key} label={pretty(key)}>
                    <Text style={{color: COLORS.text}}>
                        {moneyKeys.includes(key) ? money(value) : display(value)}
                    </Text>
                    {data[`${key}_explanation`] ? (
                        <div style={{marginTop: 4}}>
                            <Text type="secondary" style={{fontSize: 11}}>
                                {data[`${key}_explanation`]}
                            </Text>
                        </div>
                    ) : null}
                </Descriptions.Item>
            ))}
        </Descriptions>
    );
};

export default function AssetRequestDetails() {
    const {id} = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [data, setData] = useState(null);
    const [actionModal, setActionModal] = useState(null);
    const [comments, setComments] = useState("");

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosinstance.get(`/assets/assetrequestopen/${id}`);

            if (res.data.status !== 1) {
                message.warning(res.data.msg || "Unable to load asset request.");
                return;
            }

            setData(res.data.data || null);
        } catch (e) {
            message.error(
                e?.response?.data?.msg ||
                e?.response?.data?.message ||
                "Unable to load asset request."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const canEdit = useMemo(() => {
        if (!data) return false;
        return normalize(data.current_level) === "LOCAL" &&
            ["DRAFT", "RETURNED"].includes(normalize(data.status));
    }, [data]);

    const workflowAction = useMemo(() => {
        if (!data) return null;

        const level = normalize(data.current_level);
        const status = normalize(data.status);

        if (["DRAFT", "APPROVED", "REJECTED", "CANCELLED"].includes(status)) return null;

        if (level === "DISTRICT") {
            return {
                type: "forward",
                title: "District Assessment",
                button: "Approve & Forward to Area",
                endpoint: `/assets/assetrequestforward/${id}`
            };
        }

        if (level === "REGION" || level === "AREA") {
            return {
                type: "forward",
                title: "Area Assessment",
                button: "Approve & Forward to National",
                endpoint: `/assets/assetrequestforward/${id}`
            };
        }

        if (level === "NATIONAL") {
            return {
                type: "approve",
                title: "National Final Approval",
                button: "Approve Request",
                endpoint: `/assets/assetrequestapprove/${id}`
            };
        }

        return null;
    }, [data, id]);

    const runAction = async () => {
        if (!actionModal) return;

        try {
            setActionLoading(true);

            let payload = {comments};
            if (actionModal.type === "forward") payload.feedback = comments;
            if (actionModal.type === "return") payload = {reason: comments};
            if (actionModal.type === "reject") payload = {reason: comments};

            const res = await axiosinstance.post(actionModal.endpoint, payload);

            if (res.data.status !== 1) {
                return message.warning(res.data.msg || "Unable to process request.");
            }

            message.success(actionModal.success || "Request processed successfully.");
            setActionModal(null);
            setComments("");
            loadData();
        } catch (e) {
            message.error(
                e?.response?.data?.msg ||
                e?.response?.data?.message ||
                "Unable to process request."
            );
        } finally {
            setActionLoading(false);
        }
    };

    const openPrintView = () => {
        window.open(`/assets/assetrequestprint/${id}`, "_blank");
    };

    const exportExcel = () => {
        if (!data) return;

        const rows = [
            ["ASSET REQUEST REPORT", ""],
            ["Request No", display(data.request_no)],
            ["Asset Type", display(data.asset_type)],
            ["Status", pretty(data.status)],
            ["Priority", display(data.priority_level)],
            ["Current Level", LEVEL_LABELS[normalize(data.current_level)] || display(data.current_level)],
            ["Request Date", data.request_date ? dayjs(data.request_date).format("DD MMM YYYY") : "-"],
            ["Required By", data.required_by_date ? dayjs(data.required_by_date).format("DD MMM YYYY") : "-"],
            ["Quantity", display(data.quantity)],
            ["Estimated Cost", Number(data.estimated_cost || 0)],
            ["Funding Source", display(data.funding_source)],
            ["Amount Available", Number(data.amount_available || 0)],
            ["Emergency Fund", Number(data.emergency_fund_available || 0)],
            ["Building Fund", Number(data.building_fund_available || 0)],
            ["Justification", display(data.justification)],
            ["Description", display(data.asset_description)],
            ["Created By", data.created_by_name || data.creator_name || display(data.created_by)],
            ["Created At", data.created_at ? dayjs(data.created_at).format("DD MMM YYYY, hh:mm A") : "-"]
        ];

        const csv = rows.map(row => row.map(cell => {
            const value = cell === null || cell === undefined ? "" : String(cell);
            return `"${value.replace(/"/g, '""')}"`;
        }).join(",")).join("\n");

        const blob = new Blob(["\uFEFF" + csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${data.request_no || `asset-request-${data.id}`}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success("Excel-compatible report exported.");
    };

    if (loading) {
        return (
            <div style={{minHeight: 520, display: "grid", placeItems: "center", background: COLORS.background}}>
                <div style={{textAlign: "center"}}>
                    <Spin size="large"/>
                    <div style={{marginTop: 12}}>
                        <Text type="secondary">Loading asset request...</Text>
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return <Empty description="Asset request not found" style={{paddingTop: 100}}/>;
    }

    const specs = jsonObject(data.asset_specifications);
    const futureCosts = jsonObject(data.future_costs);
    const income = jsonObject(data.income_potential);
    const checklist = jsonObject(data.financial_checklist);
    const risks = jsonObject(data.risk_assessment);
    const documents = data.documents || [];
    const workflow = data.workflow || [];

    /*
     * SAME CALCULATION MODEL AS AssetAcquisitionWorkspace INPUT SCREEN.
     * Keep this block synchronized with the input screen metrics useMemo.
     */
    const checklistItemsForReview = [
        "sufficient_funds", "emergency_reserves", "budget_approval",
        "positive_cash_flow", "loan_affordability", "property_valuation",
        "legal_due_diligence", "insurance_reviewed", "future_maintenance",
        "strategic_fit", "board_approval", "risk_assessment_completed",
    ];

    const riskItemsForReview = [
        "economic_downturns", "membership_fluctuations", "giving_pattern_changes",
        "unexpected_repair_costs", "interest_rate_increases",
    ];

    const nationHelpRaw = normalize(data.require_assistance_from_nation);
    const nationHelp =
        data.require_assistance_from_nation === true ||
        data.require_assistance_from_nation === 1 ||
        nationHelpRaw === "1" ||
        nationHelpRaw === "YES" ||
        nationHelpRaw === "TRUE";

    const estimatedCost = Number(data.estimated_cost || 0);

    // Exact input-screen funding rule.
    const pledgedFunds = nationHelp
        ? Number(data.available_funds || 0)
        : Number(data.amount_available || 0);

    const emergencyFund = Number(data.emergency_fund_available || 0);
    const buildingFund = Number(data.building_fund_available || 0);
    const availableFunds = pledgedFunds + emergencyFund + buildingFund;

    const monthlyIncome = Number(data.average_monthly_tithes || 0);
    const monthlyExpense = Number(data.average_monthly_expense || 0);
    const anticipatedAmount = Number(data.anticipated_amount || 0);

    const liquidityRatio = monthlyExpense > 0 ? availableFunds / monthlyExpense : 0;
    const debtIncomeRatio = monthlyIncome > 0
        ? (anticipatedAmount / (monthlyIncome * 12)) * 100
        : 0;
    const operatingReserveMonths = monthlyExpense > 0 ? availableFunds / monthlyExpense : 0;
    const netAssetPosition = availableFunds - anticipatedAmount;

    const fundingGap = Math.max(estimatedCost - availableFunds, 0);
    const fundingPercent = estimatedCost > 0
        ? Math.min(Math.round((availableFunds / estimatedCost) * 100), 100)
        : 0;

    // Exact input-screen checklist calculation.
    const checklistAnswers = checklistItemsForReview
        .map((name) => checklist?.[name])
        .filter(Boolean);
    const checklistYes = checklistAnswers.filter((value) => value === "Yes").length;
    const checklistCompletion = Math.round(
        (checklistAnswers.length / checklistItemsForReview.length) * 100
    );
    const checklistHealth = checklistItemsForReview.length > 0
        ? Math.round((checklistYes / checklistItemsForReview.length) * 100)
        : 0;

    // Exact input-screen risk calculation.
    const riskYesCount = riskItemsForReview
        .filter((name) => risks?.[name] === "Yes")
        .length;
    const checklistGapCount =
        checklistItemsForReview.length -
        checklistYes -
        (checklistItemsForReview.length - checklistAnswers.length);
    const riskScore = Math.max(
        100 - riskYesCount * 10 - Math.max(checklistGapCount, 0) * 5,
        15
    );

    // Exact input-screen weighted readiness.
    const readinessScore = Math.round(
        fundingPercent * 0.45 +
        checklistHealth * 0.30 +
        riskScore * 0.25
    );

    const approvalLevel =
        estimatedCost >= 100000 ? "National" :
            estimatedCost >= 50000 ? "Area" :
                estimatedCost >= 10000 ? "District" : "Local";

    const fundingSource = data.funding_source || "";
    const budgetAllocationStatus = availableFunds >= estimatedCost ? "Available" : "Insufficient";
    const fundingSourceStatus = fundingSource ? "Confirmed" : "Pending";
    const affordabilityStatus = fundingGap <= 0 ? "Affordable" : "Not Affordable";
    const isAffordable = liquidityRatio >= 2 && operatingReserveMonths >= 3;

    const budgetAllocationRemarks = availableFunds >= estimatedCost
        ? "Sufficient budget is available to cover the acquisition."
        : `Additional ${money(fundingGap)} funding is required.`;
    const fundingSourceRemarks = fundingSource
        ? `${fundingSource} has been identified as the primary funding source.`
        : "No funding source has been specified.";
    const affordabilityRemarks = isAffordable
        ? "Financial position indicates that the acquisition is affordable."
        : "Financial capacity should be reviewed before approval.";
    const financeRecommendation =
        availableFunds >= estimatedCost && isAffordable ? "APPROVED" : "REVIEW REQUIRED";

    // Existing details-page aliases.
    const cost = estimatedCost;
    const available = availableFunds;
    const fundingPct = fundingPercent;

    const org = {
        company: data.company_name || data.company?.name || data.com_name || (data.com_id ? `Company #${data.com_id}` : "-"),
        nation: data.nation_name || data.nation?.name || (data.nation_id ? `Nation #${data.nation_id}` : "-"),
        region: data.region_name || data.region?.name || (data.region_id ? `Area #${data.region_id}` : "-"),
        district: data.district_name || data.district?.name || (data.district_id ? `District #${data.district_id}` : "-"),
        local: data.local_name || data.local?.name || (data.local_id ? `Local #${data.local_id}` : "-")
    };

    const reviewStrengths = [];
    const reviewConcerns = [];

    if (budgetAllocationStatus === "Available") reviewStrengths.push(budgetAllocationRemarks);
    else reviewConcerns.push(budgetAllocationRemarks);

    if (fundingSourceStatus === "Confirmed") reviewStrengths.push(fundingSourceRemarks);
    else reviewConcerns.push(fundingSourceRemarks);

    if (isAffordable) reviewStrengths.push(affordabilityRemarks);
    else reviewConcerns.push(affordabilityRemarks);

    if (checklistCompletion < 100) {
        reviewConcerns.push(`Financial checklist is ${checklistCompletion}% complete.`);
    }
    if (riskYesCount > 0) {
        reviewConcerns.push(`${riskYesCount} risk item${riskYesCount === 1 ? "" : "s"} flagged Yes.`);
    }
    if (documents.length) {
        reviewStrengths.push(`${documents.length} supporting document${documents.length === 1 ? "" : "s"} attached.`);
    } else {
        reviewConcerns.push("No supporting documents are attached.");
    }

    const reviewState = financeRecommendation === "APPROVED"
        ? {label: "APPROVED", color: "green", type: "success"}
        : {label: "REVIEW REQUIRED", color: "orange", type: "warning"};

    const exportMenu = (
        <Menu>
            <Menu.Item key="pdf" icon={<FilePdfOutlined/>} onClick={openPrintView}>
                Print / Save as PDF
            </Menu.Item>
            <Menu.Item key="excel" icon={<FileExcelOutlined/>} onClick={exportExcel}>
                Export to Excel
            </Menu.Item>
        </Menu>
    );

    return (
        <div className="asset-details-report-ui">
            <style>{`
                .asset-details-report-ui {
                    min-height: 100vh;
                    padding: 20px;
                    background: ${COLORS.background};
                    color: ${COLORS.text};
                    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
                }

                .asset-details-report-ui .report-shell {
                    max-width: 1540px;
                    margin: 0 auto;
                }

                .asset-details-report-ui .hero {
                    position: relative;
                    overflow: hidden;
                    padding: 26px 28px;
                    border-radius: 14px 14px 0 0;
                    background: linear-gradient(120deg, ${COLORS.navyDark} 0%, ${COLORS.navy} 58%, #245B8D 100%);
                    color: #fff;
                    box-shadow: 0 6px 22px rgba(16,46,80,.10);
                }

                .asset-details-report-ui .hero:before,
                .asset-details-report-ui .hero:after {
                    content: "";
                    position: absolute;
                    border-radius: 50%;
                    border-style: solid;
                    border-color: rgba(255,255,255,.045);
                }

                .asset-details-report-ui .hero:before {
                    width: 220px;
                    height: 220px;
                    border-width: 42px;
                    right: -90px;
                    top: -120px;
                }

                .asset-details-report-ui .hero:after {
                    width: 145px;
                    height: 145px;
                    border-width: 30px;
                    right: 150px;
                    bottom: -120px;
                }

                .asset-details-report-ui .hero-content {
                    position: relative;
                    z-index: 2;
                }

                .asset-details-report-ui .hero-org {
                    font-size: 12px;
                    opacity: .72;
                    margin-bottom: 11px;
                }

                .asset-details-report-ui .hero-title {
                    color: #fff !important;
                    margin: 0 !important;
                    font-size: 26px !important;
                    line-height: 1.2 !important;
                    font-weight: 750 !important;
                }

                .asset-details-report-ui .hero-subtitle {
                    color: rgba(255,255,255,.76);
                    font-size: 12px;
                    margin-top: 7px;
                }

                .asset-details-report-ui .hero-pill {
                    display: inline-flex;
                    align-items: center;
                    min-height: 28px;
                    padding: 4px 11px;
                    border-radius: 20px;
                    border: 1px solid rgba(255,255,255,.16);
                    background: rgba(255,255,255,.11);
                    color: #fff;
                    font-size: 11px;
                }

                .asset-details-report-ui .hero .ant-btn {
                    border-radius: 8px;
                }

                .asset-details-report-ui .hero .hero-light-btn {
                    background: rgba(255,255,255,.10);
                    border-color: rgba(255,255,255,.28);
                    color: #fff;
                }

                .asset-details-report-ui .hero .hero-light-btn:hover {
                    background: rgba(255,255,255,.18);
                    border-color: rgba(255,255,255,.42);
                    color: #fff;
                }

                .asset-details-report-ui .meta-strip {
                    padding: 12px 20px;
                    background: #fff;
                    border: 1px solid ${COLORS.border};
                    border-top: none;
                    border-radius: 0 0 12px 12px;
                    box-shadow: 0 4px 18px rgba(16,46,80,.045);
                    margin-bottom: 14px;
                }

                .asset-details-report-ui .meta-item {
                    min-height: 38px;
                    padding-right: 14px;
                    border-right: 1px solid ${COLORS.lightBorder};
                }

                .asset-details-report-ui .meta-label {
                    font-size: 9px;
                    text-transform: uppercase;
                    letter-spacing: .55px;
                    color: ${COLORS.secondary};
                }

                .asset-details-report-ui .meta-value {
                    margin-top: 3px;
                    font-size: 12px;
                    font-weight: 650;
                    color: ${COLORS.text};
                }

                .asset-details-report-ui .kpi-card {
                    height: 100%;
                    min-height: 108px;
                    padding: 15px;
                    border-radius: 10px;
                    border: 1px solid ${COLORS.border};
                    transition: transform .18s ease, box-shadow .18s ease;
                }

                .asset-details-report-ui .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 7px 20px rgba(16,46,80,.08);
                }

                .asset-details-report-ui .kpi-blue { background: ${COLORS.blueLight}; }
                .asset-details-report-ui .kpi-green { background: ${COLORS.greenLight}; }
                .asset-details-report-ui .kpi-orange { background: ${COLORS.orangeLight}; }
                .asset-details-report-ui .kpi-neutral { background: #F7F9FB; }
                .asset-details-report-ui .kpi-purple { background: ${COLORS.purpleLight}; }

                .asset-details-report-ui .kpi-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 8px;
                }

                .asset-details-report-ui .kpi-label {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: .45px;
                    color: ${COLORS.secondary};
                }

                .asset-details-report-ui .kpi-icon {
                    width: 30px;
                    height: 30px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,.65);
                    color: ${COLORS.navy};
                    font-size: 14px;
                }

                .asset-details-report-ui .kpi-value {
                    margin-top: 9px;
                    font-size: 20px;
                    line-height: 1.15;
                    font-weight: 750;
                    color: ${COLORS.navyDark};
                }

                .asset-details-report-ui .kpi-helper {
                    margin-top: 5px;
                    font-size: 10px;
                    color: ${COLORS.secondary};
                }

                .asset-details-report-ui .coverage-card {
                    height: 100%;
                    min-height: 108px;
                    padding: 15px;
                    border-radius: 10px;
                    border: 1px solid ${COLORS.border};
                    background: #fff;
                }

                .asset-details-report-ui .coverage-value {
                    font-size: 20px;
                    font-weight: 750;
                    color: ${COLORS.navyDark};
                }

                .asset-details-report-ui .section-heading {
                    min-height: 57px;
                    padding: 11px 16px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    background: linear-gradient(90deg, #F8FAFC 0%, #FFFFFF 68%);
                    border-bottom: 1px solid ${COLORS.border};
                    border-left: 3px solid ${COLORS.navy};
                }

                .asset-details-report-ui .section-heading-left {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .asset-details-report-ui .section-icon {
                    width: 31px;
                    height: 31px;
                    border-radius: 7px;
                    background: ${COLORS.blueLight};
                    color: ${COLORS.navy};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 14px;
                }

                .asset-details-report-ui .section-title {
                    font-size: 13px;
                    line-height: 1.25;
                    font-weight: 700;
                    color: ${COLORS.navyDark};
                }

                .asset-details-report-ui .section-subtitle {
                    margin-top: 2px;
                    font-size: 10px;
                    color: ${COLORS.secondary};
                }

                .asset-details-report-ui .section-body {
                    padding: 16px;
                    background: #fff;
                }

                .asset-details-report-ui .report-descriptions .ant-descriptions-view {
                    overflow: hidden;
                    border-radius: 7px;
                    border-color: ${COLORS.border};
                }

                .asset-details-report-ui .report-descriptions .ant-descriptions-item-label {
                    background: ${COLORS.light} !important;
                    color: ${COLORS.secondary} !important;
                    font-size: 10px !important;
                    font-weight: 500 !important;
                    white-space: nowrap;
                }

                .asset-details-report-ui .report-descriptions .ant-descriptions-item-content {
                    background: #fff !important;
                    color: ${COLORS.text} !important;
                    font-size: 11.5px !important;
                }

                .asset-details-report-ui .report-descriptions .ant-descriptions-item-label,
                .asset-details-report-ui .report-descriptions .ant-descriptions-item-content {
                    border-color: ${COLORS.border} !important;
                    padding: 8px 10px !important;
                }

                .asset-details-report-ui .document-card {
                    height: 100%;
                    border-radius: 9px;
                    border: 1px solid ${COLORS.border};
                    transition: .18s ease;
                }

                .asset-details-report-ui .document-card:hover {
                    border-color: #B9CDE2;
                    box-shadow: 0 5px 16px rgba(16,46,80,.06);
                }

                .asset-details-report-ui .doc-icon {
                    width: 40px;
                    height: 40px;
                    min-width: 40px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: ${COLORS.blueLight};
                    color: ${COLORS.navy};
                    font-size: 18px;
                }

                .asset-details-report-ui .workflow-item-box {
                    border: 1px solid ${COLORS.lightBorder};
                    border-radius: 8px;
                    padding: 10px 12px;
                    background: ${COLORS.light};
                }

                .asset-details-report-ui .workflow-timeline .ant-timeline-item-tail {
                    border-left: 1px solid ${COLORS.border};
                }

                .asset-details-report-ui .ant-modal-content,
                .asset-details-report-ui .ant-alert {
                    border-radius: 9px;
                }

                @media (max-width: 991px) {
                    .asset-details-report-ui { padding: 12px; }
                    .asset-details-report-ui .hero { padding: 20px; }
                    .asset-details-report-ui .hero-actions { margin-top: 16px; }
                    .asset-details-report-ui .meta-item { border-right: 0; }
                }

                @media (max-width: 575px) {
                    .asset-details-report-ui { padding: 8px; }
                    .asset-details-report-ui .hero { padding: 17px; }
                    .asset-details-report-ui .hero-title { font-size: 21px !important; }
                    .asset-details-report-ui .section-body { padding: 11px; }
                }
            `}</style>

            <div className="report-shell">
                <div className="hero">
                    <div className="hero-content">
                        <Row gutter={[18, 18]} justify="space-between" align="middle">
                            <Col xs={24} lg={14}>
                                <div className="hero-org">
                                    {org.company} • Asset Management & Approval System
                                </div>

                                <Space align="start" size={12}>
                                    <Tooltip title="Back">
                                        <Button
                                            className="hero-light-btn"
                                            icon={<ArrowLeftOutlined/>}
                                            onClick={() => history.goBack()}
                                        />
                                    </Tooltip>

                                    <div>
                                        <Title className="hero-title" level={2}>
                                            {data.request_no || `Asset Request #${data.id}`}
                                        </Title>

                                        <div className="hero-subtitle">
                                            {display(data.asset_type)}
                                            {" • "}
                                            {display(data.asset_description)}
                                        </div>

                                        <Space wrap size={[6, 6]} style={{marginTop: 12}}>
                                            <span className="hero-pill">{pretty(data.status)}</span>
                                            <span className="hero-pill">{display(data.priority_level)} Priority</span>
                                            <span className="hero-pill">
                                                {LEVEL_LABELS[normalize(data.current_level)] || display(data.current_level)} Level
                                            </span>
                                        </Space>
                                    </div>
                                </Space>
                            </Col>

                            <Col xs={24} lg={10}>
                                <div className="hero-actions" style={{textAlign: "right"}}>
                                    <Space wrap size={8}>
                                        <Button
                                            className="hero-light-btn"
                                            icon={<PrinterOutlined/>}
                                            onClick={openPrintView}
                                        >
                                            Print
                                        </Button>

                                        <Dropdown overlay={exportMenu} trigger={["click"]}>
                                            <Button className="hero-light-btn" icon={<DownloadOutlined/>}>
                                                Export <DownOutlined/>
                                            </Button>
                                        </Dropdown>

                                        {canEdit && (
                                            <Button
                                                className="hero-light-btn"
                                                icon={<EditOutlined/>}
                                                onClick={() => history.push(`/assets/editassetrequest/${id}`)}
                                            >
                                                Edit
                                            </Button>
                                        )}

                                        {workflowAction && (
                                            <Button
                                                type="primary"
                                                icon={workflowAction.type === "approve" ? <CheckCircleOutlined/> : <SendOutlined/>}
                                                onClick={() => setActionModal({
                                                    ...workflowAction,
                                                    success: "Workflow action completed successfully."
                                                })}
                                                style={{
                                                    background: "#FFFFFF",
                                                    borderColor: "#FFFFFF",
                                                    color: COLORS.navyDark,
                                                    fontWeight: 650
                                                }}
                                            >
                                                {workflowAction.button}
                                            </Button>
                                        )}

                                        {workflowAction && normalize(data.current_level) !== "LOCAL" && (
                                            <Button
                                                className="hero-light-btn"
                                                onClick={() => setActionModal({
                                                    type: "return",
                                                    title: `Return from ${LEVEL_LABELS[normalize(data.current_level)] || display(data.current_level)}`,
                                                    button: "Return Request",
                                                    endpoint: `/assets/assetrequestreturn/${id}`,
                                                    success: "Request returned successfully."
                                                })}
                                            >
                                                Return
                                            </Button>
                                        )}

                                        {workflowAction && normalize(data.current_level) !== "LOCAL" && (
                                            <Button
                                                danger
                                                onClick={() => setActionModal({
                                                    type: "reject",
                                                    title: "Reject Asset Request",
                                                    button: "Reject",
                                                    endpoint: `/assets/assetrequestreject/${id}`,
                                                    success: "Request rejected."
                                                })}
                                                style={{borderRadius: 8}}
                                            >
                                                Reject
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </div>

                <div className="meta-strip">
                    <Row gutter={[16, 12]}>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item">
                                <div className="meta-label">Request Date</div>
                                <div className="meta-value">
                                    {data.request_date ? dayjs(data.request_date).format(CisUI().dateFormat) : "-"}
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item">
                                <div className="meta-label">Required By</div>
                                <div className="meta-value">
                                    {data.required_by_date ? dayjs(data.required_by_date).format(CisUI().dateFormat) : "-"}
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item">
                                <div className="meta-label">Local</div>
                                <div className="meta-value">{org.local}</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item">
                                <div className="meta-label">District</div>
                                <div className="meta-value">{org.district}</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item">
                                <div className="meta-label">Area</div>
                                <div className="meta-value">{org.region}</div>
                            </div>
                        </Col>
                        <Col xs={12} sm={8} lg={4}>
                            <div className="meta-item" style={{borderRight: 0}}>
                                <div className="meta-label">Nation</div>
                                <div className="meta-value">{org.nation}</div>
                            </div>
                        </Col>
                    </Row>
                </div>

                <Row gutter={[12, 12]} style={{marginBottom: 16}}>
                    <Col xs={24} sm={12} xl={5}>
                        <KPI
                            label="Estimated Cost"
                            value={cost}
                            helper="Requested investment"
                            icon={<DollarOutlined/>}
                            variant="blue"
                        />
                    </Col>
                    <Col xs={24} sm={12} xl={5}>
                        <KPI
                            label="Available Funds"
                            value={available}
                            helper="Combined available funding"
                            icon={<WalletOutlined/>}
                            variant="green"
                        />
                    </Col>
                    <Col xs={24} sm={12} xl={5}>
                        <KPI
                            label="Funding Gap"
                            value={fundingGap}
                            helper={fundingGap > 0 ? "Additional funding required" : "Fully funded"}
                            icon={<BankOutlined/>}
                            variant={fundingGap > 0 ? "orange" : "green"}
                        />
                    </Col>
                    <Col xs={24} sm={12} xl={4}>
                        <KPI
                            label="Adult Members"
                            value={Number(data.adult_membership || 0)}
                            helper={`${Number(data.tithe_paying_members || 0).toLocaleString()} tithe paying`}
                            icon={<TeamOutlined/>}
                            variant="purple"
                            formatter={(v) => Number(v || 0).toLocaleString()}
                        />
                    </Col>
                    <Col xs={24} xl={5}>
                        <div className="coverage-card">
                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                <div>
                                    <div className="kpi-label">Funding Coverage</div>
                                    <div className="coverage-value">{fundingPct}%</div>
                                </div>
                                <div className="kpi-icon"><CheckCircleOutlined/></div>
                            </div>
                            <Progress
                                percent={fundingPct}
                                showInfo={false}
                                status={fundingPct >= 100 ? "success" : "active"}
                                strokeWidth={7}
                                style={{marginTop: 7, marginBottom: 2}}
                            />
                            <div className="kpi-helper">
                                {fundingPct >= 100 ? "Funding requirement covered" : `${money(fundingGap)} remaining`}
                            </div>
                        </div>
                    </Col>
                </Row>

                <Card
                    bordered={false}
                    style={{...pageCard, marginBottom: 16}}
                    bodyStyle={{padding: 0}}
                >
                    <div style={{
                        padding: "14px 16px",
                        background: `linear-gradient(90deg, ${COLORS.navyDark} 0%, ${COLORS.navy} 100%)`,
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12
                    }}>
                        <div>
                            <div style={{fontSize: 14, fontWeight: 750, color: "#fff"}}>
                                Executive Review for Approval Decision
                            </div>
                            <div style={{fontSize: 10, color: "rgba(255,255,255,.72)", marginTop: 2}}>
                                Same financial calculation model used on the Asset Acquisition input review screen
                            </div>
                        </div>
                        <Tag color={reviewState.color} style={{margin: 0, width: "150px", fontWeight: 700}}>
                            {reviewState.label}
                        </Tag>
                    </div>

                    <div style={{padding: 16}}>
                        <Alert
                            type={reviewState.type}
                            // showIcon
                            style={{marginBottom: 12}}
                            message={
                                financeRecommendation === "APPROVED"
                                    ? "Finance calculation indicates APPROVED based on the same funding and affordability rules used on the input screen."
                                    : "Finance calculation indicates REVIEW REQUIRED. Review funding and financial capacity before the approval decision."
                            }
                        />

                        <Row gutter={[10, 10]}>
                            {[
                                ["Readiness Score", `${readinessScore}%`, "45% funding + 30% checklist + 25% risk"],
                                ["Funding Readiness", `${fundingPercent}%`, fundingGap > 0 ? `${money(fundingGap)} gap` : "Fully covered"],
                                ["Checklist Health", `${checklistHealth}%`, `${checklistYes}/${checklistItemsForReview.length} Yes`],
                                ["Risk Score", `${riskScore}%`, `${riskYesCount} risk item${riskYesCount === 1 ? "" : "s"} flagged`],
                                ["Approval Level", approvalLevel, "Based on estimated cost"],
                                ["Finance Recommendation", financeRecommendation, "Same rule as input review"]
                            ].map(([label, value, note]) => (
                                <Col xs={12} md={8} xl={4} key={label}>
                                    <div style={{
                                        height: "100%", minHeight: 88, padding: 12, borderRadius: 9,
                                        border: `1px solid ${COLORS.lightBorder}`, background: COLORS.light
                                    }}>
                                        <div style={{fontSize: 9, textTransform: "uppercase", letterSpacing: .45, color: COLORS.secondary}}>
                                            {label}
                                        </div>
                                        <div style={{fontSize: label === "Finance Recommendation" ? 12 : 16, fontWeight: 750, color: COLORS.navyDark, marginTop: 5}}>
                                            {value}
                                        </div>
                                        <div style={{fontSize: 10, color: COLORS.secondary, marginTop: 4}}>
                                            {note}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={[12, 12]} style={{marginTop: 12}}>
                            <Col xs={24} lg={12}>
                                <div style={{height: "100%", padding: 13, border: `1px solid ${COLORS.lightBorder}`, borderRadius: 9}}>
                                    <Text strong style={{fontSize: 10, color: COLORS.navyDark}}>FINANCE DEPARTMENT REVIEW</Text>
                                    <div style={{marginTop: 8}}>
                                        {[
                                            ["Budget Allocation", budgetAllocationStatus, budgetAllocationRemarks, budgetAllocationStatus === "Available"],
                                            ["Funding Source", fundingSourceStatus, fundingSourceRemarks, fundingSourceStatus === "Confirmed"],
                                            ["Affordability", affordabilityStatus, affordabilityRemarks, isAffordable],
                                        ].map(([label, status, remark, ok]) => (
                                            <div key={label} style={{display: "flex", gap: 8, marginBottom: 9, fontSize: 11}}>
                                                {ok
                                                    ? <CheckCircleOutlined style={{color: COLORS.green, marginTop: 2}}/>
                                                    : <SafetyCertificateOutlined style={{color: COLORS.orange, marginTop: 2}}/>
                                                }
                                                <span><strong>{label}: {status}</strong><br/>{remark}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Col>

                            <Col xs={24} lg={12}>
                                <div style={{height: "100%", padding: 13, border: `1px solid ${COLORS.lightBorder}`, borderRadius: 9}}>
                                    <Text strong style={{fontSize: 10, color: COLORS.navyDark}}>CALCULATED FINANCIAL INDICATORS</Text>
                                    <Descriptions size="small" column={1} style={{marginTop: 8}}>
                                        <Descriptions.Item label="Available Funds">{money(availableFunds)}</Descriptions.Item>
                                        <Descriptions.Item label="Funding Gap">{money(fundingGap)}</Descriptions.Item>
                                        <Descriptions.Item label="Liquidity Ratio">{liquidityRatio.toFixed(2)}x</Descriptions.Item>
                                        <Descriptions.Item label="Operating Reserve">{operatingReserveMonths.toFixed(2)} months</Descriptions.Item>
                                        <Descriptions.Item label="Debt / Income Ratio">{debtIncomeRatio.toFixed(2)}%</Descriptions.Item>
                                    </Descriptions>
                                </div>
                            </Col>
                        </Row>

                        <Row gutter={[12, 12]} style={{marginTop: 12}}>
                            <Col xs={24} lg={12}>
                                <div style={{height: "100%", padding: 13, border: `1px solid ${COLORS.lightBorder}`, borderRadius: 9}}>
                                    <Text strong style={{fontSize: 10, color: COLORS.navyDark}}>POSITIVE DECISION FACTORS</Text>
                                    <div style={{marginTop: 8}}>
                                        {reviewStrengths.length ? reviewStrengths.map((item, index) => (
                                            <div key={index} style={{display: "flex", gap: 8, marginBottom: 7, fontSize: 11}}>
                                                <CheckCircleOutlined style={{color: COLORS.green, marginTop: 2}}/>
                                                <span>{item}</span>
                                            </div>
                                        )) : <Text type="secondary" style={{fontSize: 11}}>No positive factor identified.</Text>}
                                    </div>
                                </div>
                            </Col>

                            <Col xs={24} lg={12}>
                                <div style={{height: "100%", padding: 13, border: `1px solid ${COLORS.lightBorder}`, borderRadius: 9}}>
                                    <Text strong style={{fontSize: 10, color: COLORS.navyDark}}>APPROVER ATTENTION</Text>
                                    <div style={{marginTop: 8}}>
                                        {reviewConcerns.length ? reviewConcerns.map((item, index) => (
                                            <div key={index} style={{display: "flex", gap: 8, marginBottom: 7, fontSize: 11}}>
                                                <SafetyCertificateOutlined style={{color: COLORS.orange, marginTop: 2}}/>
                                                <span>{item}</span>
                                            </div>
                                        )) : <Text type="secondary" style={{fontSize: 11}}>No additional attention item identified.</Text>}
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        <div style={{
                            marginTop: 12, padding: "9px 11px", borderRadius: 8,
                            background: COLORS.blueLight, border: "1px solid #D7E6F3"
                        }}>
                            <Text style={{fontSize: 10.5, color: COLORS.secondary}}>
                                <strong style={{color: COLORS.navyDark}}>Calculation basis:</strong>{" "}
                                Available Funds = {nationHelp ? "Nation Available Funds" : "Amount Available"} + Emergency Fund + Building Fund.
                                Readiness = Funding Coverage 45% + Checklist Health 30% + Risk Score 25%.
                                Finance recommendation is APPROVED only when available funds cover estimated cost and
                                liquidity / operating reserve affordability passes.
                            </Text>
                        </div>
                    </div>
                </Card>

                <Section
                    title="Entity & Ownership"
                    subtitle="Organizational ownership and approval jurisdiction"
                    icon={<HomeOutlined/>}
                >
                    <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 3}} className="report-descriptions">
                        <Descriptions.Item label="Company">{org.company}</Descriptions.Item>
                        <Descriptions.Item label="Nation">{org.nation}</Descriptions.Item>
                        <Descriptions.Item label="Area">{org.region}</Descriptions.Item>
                        <Descriptions.Item label="District">{org.district}</Descriptions.Item>
                        <Descriptions.Item label="Local">{org.local}</Descriptions.Item>
                        <Descriptions.Item label="Current Approval Level">
                            <Tag color="geekblue">
                                {LEVEL_LABELS[normalize(data.current_level)] || display(data.current_level)}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>
                </Section>

                <Section
                    title="Request Information"
                    subtitle="Core request and acquisition information"
                    icon={<FileTextOutlined/>}
                >
                    <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 3}} className="report-descriptions">
                        <Descriptions.Item label="Request No">
                            <Text strong>{display(data.request_no)}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Asset Type">{display(data.asset_type)}</Descriptions.Item>
                        <Descriptions.Item label="Request Date">
                            {data.request_date ? dayjs(data.request_date).format(CisUI().dateFormat) : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Required By">
                            {data.required_by_date ? dayjs(data.required_by_date).format(CisUI().dateFormat) : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Priority">
                            <Tag color={PRIORITY_COLORS[normalize(data.priority_level)] || "default"}>
                                {display(data.priority_level)}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Acquisition Method">{display(data.acquisition_method)}</Descriptions.Item>
                        <Descriptions.Item label="Quantity">{display(data.quantity)}</Descriptions.Item>
                        <Descriptions.Item label="Estimated Cost">
                            <Text strong>{money(data.estimated_cost)}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Nation Assistance">
                            {Number(data.require_assistance_from_nation) === 1
                                ? <Tag color="orange">Required</Tag>
                                : <Tag color="green">Not Required</Tag>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Justification" span={3}>
                            <Paragraph style={{margin: 0}}>{display(data.justification)}</Paragraph>
                        </Descriptions.Item>
                        <Descriptions.Item label="Description" span={3}>
                            <Paragraph style={{margin: 0}}>{display(data.asset_description)}</Paragraph>
                        </Descriptions.Item>
                    </Descriptions>
                </Section>

                <Section
                    title="Asset Specifications"
                    subtitle="Technical and asset-specific information"
                    icon={<SafetyCertificateOutlined/>}
                >
                    <JsonDescriptions
                        data={specs}
                        moneyKeys={["property_value", "asset_value", "unit_cost", "current_book_value"]}
                    />
                </Section>

                <Section
                    title="Funding & Financing"
                    subtitle="Funding availability, financing and repayment structure"
                    icon={<BankOutlined/>}
                >
                    <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 3}} className="report-descriptions">
                        <Descriptions.Item label="Funding Source">{display(data.funding_source)}</Descriptions.Item>
                        <Descriptions.Item label="Amount Available"><Text strong>{money(data.amount_available)}</Text></Descriptions.Item>
                        <Descriptions.Item label="Payment Mode">{display(data.payment_mode)}</Descriptions.Item>
                        <Descriptions.Item label="Anticipated Amount">{money(data.anticipated_amount)}</Descriptions.Item>
                        <Descriptions.Item label="Available Funds">{money(data.available_funds)}</Descriptions.Item>
                        <Descriptions.Item label="Interest Rate">
                            {data.interest_rate !== null && data.interest_rate !== undefined ? `${data.interest_rate}%` : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Loan Term">{display(data.loan_term)}</Descriptions.Item>
                        <Descriptions.Item label="Monthly Payment">{money(data.monthly_payment)}</Descriptions.Item>
                        <Descriptions.Item label="Rate Type">{display(data.rate_type)}</Descriptions.Item>
                    </Descriptions>
                </Section>

                <Section
                    title="Cash Flow & Financial Capacity"
                    subtitle="Membership, income, expenses and reserve funds"
                    icon={<WalletOutlined/>}
                >
                    <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 4}} className="report-descriptions">
                        <Descriptions.Item label="Adult Membership">{display(data.adult_membership)}</Descriptions.Item>
                        <Descriptions.Item label="Tithe Paying Members">{display(data.tithe_paying_members)}</Descriptions.Item>
                        <Descriptions.Item label="Avg. Monthly Tithes">{money(data.average_monthly_tithes)}</Descriptions.Item>
                        <Descriptions.Item label="Avg. Monthly Expense">{money(data.average_monthly_expense)}</Descriptions.Item>
                        <Descriptions.Item label="Monthly Remittance">{money(data.average_monthly_remittance)}</Descriptions.Item>
                        <Descriptions.Item label="Coffers / LDF">{money(data.amount_in_coffers_ldf)}</Descriptions.Item>
                        <Descriptions.Item label="Emergency Fund">{money(data.emergency_fund_available)}</Descriptions.Item>
                        <Descriptions.Item label="Building Fund">{money(data.building_fund_available)}</Descriptions.Item>
                    </Descriptions>
                </Section>

                <Section title="Future Costs" subtitle="Expected future financial obligations" icon={<DollarOutlined/>}>
                    <JsonDescriptions data={futureCosts} moneyKeys={Object.keys(futureCosts)}/>
                </Section>

                <Section title="Income Potential" subtitle="Expected income and financial benefit" icon={<BankOutlined/>}>
                    <JsonDescriptions data={income}/>
                </Section>

                <Section
                    title="Financial Readiness Checklist"
                    subtitle="Financial readiness and supporting assessment"
                    icon={<CheckCircleOutlined/>}
                >
                    <JsonDescriptions data={checklist}/>
                </Section>

                <Section
                    title="Risk Assessment"
                    subtitle="Risk factors identified for this request"
                    icon={<SafetyCertificateOutlined/>}
                >
                    <JsonDescriptions data={risks}/>
                </Section>

                <Section
                    title={`Supporting Documents (${documents.length})`}
                    subtitle="Documents submitted with the request"
                    icon={<FileTextOutlined/>}
                >
                    {documents.length ? (
                        <Row gutter={[12, 12]}>
                            {documents.map(doc => {
                                const mime = String(doc.mime_type || "").toLowerCase();
                                const isImage = mime.startsWith("image/") ||
                                    /\.(jpg|jpeg|png|webp|gif)$/i.test(doc.file_name || "");
                                const isPdf = mime.includes("pdf") || /\.pdf$/i.test(doc.file_name || "");

                                return (
                                    <Col xs={24} md={12} xl={8} key={doc.id}>
                                        <Card size="small" className="document-card" bodyStyle={{padding: 12}}>
                                            <Space align="start" size={11}>
                                                <div
                                                    className="doc-icon"
                                                    style={isPdf ? {
                                                        background: COLORS.redLight,
                                                        color: COLORS.red
                                                    } : undefined}
                                                >
                                                    {isImage
                                                        ? <FileImageOutlined/>
                                                        : isPdf
                                                            ? <FilePdfOutlined/>
                                                            : <FileTextOutlined/>}
                                                </div>

                                                <div style={{minWidth: 0}}>
                                                    <Text strong ellipsis style={{display: "block", maxWidth: 280}}>
                                                        {doc.original_file_name || doc.file_name}
                                                    </Text>
                                                    <Text type="secondary" style={{fontSize: 10}}>
                                                        {pretty(doc.document_type)}
                                                    </Text>

                                                    {isImage && doc.file_url ? (
                                                        <div style={{marginTop: 8}}>
                                                            <Image
                                                                width={72}
                                                                height={52}
                                                                src={doc.file_url}
                                                                style={{objectFit: "cover", borderRadius: 6}}
                                                            />
                                                        </div>
                                                    ) : null}

                                                    {doc.file_url ? (
                                                        <div style={{marginTop: 6}}>
                                                            <Button
                                                                size="small"
                                                                type="link"
                                                                icon={<DownloadOutlined/>}
                                                                style={{paddingLeft: 0}}
                                                                onClick={() => window.open(doc.file_url, "_blank")}
                                                            >
                                                                Open Document
                                                            </Button>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </Space>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No supporting documents attached"/>
                    )}
                </Section>

                <Section
                    title="Approval & Workflow History"
                    subtitle="Official review and approval trail"
                    icon={<ClockCircleOutlined/>}
                >
                    {workflow.length ? (
                        <Timeline className="workflow-timeline">
                            {workflow.map(item => {
                                const action = normalize(item.action);
                                const color = action === "APPROVED"
                                    ? "green"
                                    : action === "REJECTED"
                                        ? "red"
                                        : action === "RETURNED"
                                            ? "orange"
                                            : "blue";

                                return (
                                    <Timeline.Item key={item.id} color={color}>
                                        <div className="workflow-item-box">
                                            <Row gutter={[12, 8]}>
                                                <Col xs={24} md={17}>
                                                    <Space wrap>
                                                        <Text strong>{pretty(item.action)}</Text>
                                                        <Tag>
                                                            {LEVEL_LABELS[normalize(item.level)] || display(item.level)}
                                                        </Tag>
                                                        {item.to_level ? (
                                                            <Text type="secondary" style={{fontSize: 11}}>
                                                                → {LEVEL_LABELS[normalize(item.to_level)] || item.to_level}
                                                            </Text>
                                                        ) : null}
                                                    </Space>

                                                    {item.feedback ? (
                                                        <div style={{marginTop: 6}}>
                                                            <Text style={{fontSize: 11.5}}>{item.feedback}</Text>
                                                        </div>
                                                    ) : null}

                                                    {item.comments ? (
                                                        <div style={{marginTop: 4}}>
                                                            <Text type="secondary" style={{fontSize: 11}}>{item.comments}</Text>
                                                        </div>
                                                    ) : null}

                                                    {item.reason ? (
                                                        <Alert
                                                            style={{marginTop: 8}}
                                                            type="warning"
                                                            // showIcon
                                                            message={item.reason}
                                                        />
                                                    ) : null}
                                                </Col>

                                                <Col xs={24} md={7} style={{textAlign: "right"}}>
                                                    <Text strong style={{fontSize: 11}}>
                                                        {item.action_by_name ||
                                                            item.user_name ||
                                                            (item.action_by ? `User #${item.action_by}` : "-")}
                                                    </Text>
                                                    <div>
                                                        <Text type="secondary" style={{fontSize: 10}}>
                                                            {item.action_at
                                                                ? dayjs(item.action_at).format("DD MMM YYYY, hh:mm A")
                                                                : "-"}
                                                        </Text>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Timeline.Item>
                                );
                            })}
                        </Timeline>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No workflow history"/>
                    )}
                </Section>

                <Section
                    title="Audit Information"
                    subtitle="Record creation and last modification details"
                    icon={<UserOutlined/>}
                >
                    <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 4}} className="report-descriptions">
                        <Descriptions.Item label="Created By">
                            {data.created_by_name || data.creator_name || (data.created_by ? `User #${data.created_by}` : "-")}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created At">
                            {data.created_at ? dayjs(data.created_at).format("DD MMM YYYY, hh:mm A") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Updated By">
                            {data.updated_by_name || data.updater_name || (data.updated_by ? `User #${data.updated_by}` : "-")}
                        </Descriptions.Item>
                        <Descriptions.Item label="Updated At">
                            {data.updated_at ? dayjs(data.updated_at).format("DD MMM YYYY, hh:mm A") : "-"}
                        </Descriptions.Item>
                    </Descriptions>
                </Section>
            </div>

            <Modal
                visible={Boolean(actionModal)}
                title={actionModal?.title}
                okText={actionModal?.button || "Confirm"}
                okButtonProps={{danger: actionModal?.type === "reject"}}
                confirmLoading={actionLoading}
                onOk={runAction}
                onCancel={() => {
                    if (!actionLoading) {
                        setActionModal(null);
                        setComments("");
                    }
                }}
            >
                <Alert
                    type={actionModal?.type === "reject" || actionModal?.type === "return" ? "warning" : "info"}
                    // showIcon
                    message={`${data.request_no || `Request #${data.id}`} • ${data.asset_type}`}
                    style={{marginBottom: 16}}
                />

                <Text strong>
                    {actionModal?.type === "return" || actionModal?.type === "reject"
                        ? "Reason"
                        : "Assessment / Comments"}
                </Text>

                <textarea
                    style={{
                        width: "100%",
                        minHeight: 115,
                        marginTop: 8,
                        padding: 11,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 8,
                        outline: "none",
                        resize: "vertical",
                        fontFamily: "inherit"
                    }}
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Enter comments, feedback or reason..."
                />
            </Modal>
        </div>
    );
}

